/**
 * Budget Forecast tab on the project detail page.
 *
 * Two month-grained grids spanning the project's start → end months:
 *
 *  - Staff days: one row per (staffed employee × task the project logs time on).
 *    A project with no tasks gets one row per person; a project with tasks gets a
 *    row per task that accepts time, plus a project-level row when its timesheet
 *    config still allows untasked entry. Two display modes — "Forecast only", and
 *    "Actual + forecast" where past months show the days actually logged as
 *    read-only green cells.
 *  - Other costs: one row per cost line, each in its own currency, so totals are
 *    reported per currency rather than summed across them.
 *
 * Cells save one at a time on blur, like the timesheet and planning grids;
 * clearing a cell deletes its row server-side. No nested <form>: this tab lives
 * inside #project-form, so everything goes through fetch.
 */

import Chart from 'chart.js/auto';

/** EUR with thousands separators and no cents — these are budget figures. */
const EUR = new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'EUR',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
});

function fmtEur(value) {
    const n = parseFloat(value);
    return Number.isFinite(n) ? EUR.format(n) : '—';
}

/** "Mar 2026" from a "2026-03" key, for axis labels and indicator notes. */
function monthLabel(key) {
    const [y, m] = String(key || '').split('-');
    if (!y || !m) return key || '—';
    return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][+m - 1]} ${y}`;
}

function getCsrfToken() {
    const el = document.querySelector('[name=csrfmiddlewaretoken]');
    return el ? el.value : '';
}

/** Sum the numeric values of a {month: "1.5"} map. */
function sumValues(byMonth) {
    return Object.values(byMonth || {}).reduce(
        (acc, v) => acc + (parseFloat(v) || 0), 0,
    );
}

/** Days read best trimmed: 5, 10.5 — not 5.00 / 10.50. */
function fmtDays(n) {
    if (!n) return '';
    return parseFloat(Number(n).toFixed(2)).toString();
}

/** Money keeps its cents: 4200.50, not 4200.5. */
function fmtMoney(n) {
    if (!n) return '';
    return Number(n).toFixed(2);
}

/** A ratio as a percentage, trimmed: 13%, 12.5% — never 13.0%. */
function fmtPct(ratio) {
    return `${parseFloat((ratio * 100).toFixed(1))}%`;
}

export function initBudgetForecast() {
    const root = document.getElementById('budget-forecast-app');
    if (!root) return;

    const urls = {
        data: root.dataset.urlData,
        staffSave: root.dataset.urlStaffSave,
        costSave: root.dataset.urlCostSave,
        lineAdd: root.dataset.urlLineAdd,
        lineUpdate: root.dataset.urlLineUpdate,
        lineDelete: root.dataset.urlLineDelete,
    };

    const noDates = document.getElementById('bf-no-dates');
    const content = document.getElementById('bf-content');
    const staffTable = document.getElementById('bf-staff-table');
    const staffEmpty = document.getElementById('bf-staff-empty');
    const actualsLegend = document.getElementById('bf-actuals-legend');
    const costTable = document.getElementById('bf-cost-table');
    const costEmpty = document.getElementById('bf-cost-empty');
    const newLineInput = document.getElementById('bf-new-line');
    const newLineCurrency = document.getElementById('bf-new-line-currency');
    const addLineBtn = document.getElementById('bf-add-line-btn');
    const modeInputs = [...document.querySelectorAll('input[name="bf-mode"]')];
    const kpiWrap = document.getElementById('bf-kpis');
    const caveatBox = document.getElementById('bf-caveats');
    const caveatText = document.getElementById('bf-caveats-text');
    const chartCanvas = document.getElementById('bf-cashflow-chart');
    const chartEmpty = document.getElementById('bf-chart-empty');
    let cashflowChart = null;

    let state = {
        months: [], monthLabels: [], pastMonths: new Set(),
        staffRows: [], staffRates: {}, costRows: [], costTotals: [], costRates: {}, costEur: null,
        cashflow: null,
        hasTasks: false, withActuals: false, currencies: [], currency: 'EUR',
    };
    let loaded = false;

    async function postJson(url, payload) {
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCsrfToken(),
            },
            body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.ok === false) throw new Error(data.error || res.statusText);
        return data;
    }

    async function postForm(url, fd) {
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'X-CSRFToken': getCsrfToken() },
            body: fd,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.ok === false) throw new Error(data.error || res.statusText);
        return data;
    }

    function currencySelect(selected) {
        const sel = document.createElement('select');
        sel.className = 'bf-currency';
        for (const code of state.currencies) {
            const opt = document.createElement('option');
            opt.value = code;
            opt.textContent = code;
            if (code === selected) opt.selected = true;
            sel.appendChild(opt);
        }
        return sel;
    }

    // --- shared table scaffolding ------------------------------------------

    /**
     * Header row. `leading` names the sticky columns on the left, so the staff
     * grid can carry a Task column only when the project has tasks; `totals`
     * names the total columns on the right, so the staff grid can carry both a
     * days total and its money equivalent.
     */
    function buildHead(table, leading, hasActions, totals = ['Total']) {
        const thead = table.querySelector('thead');
        thead.innerHTML = '';
        const tr = document.createElement('tr');
        leading.forEach((label, i) => {
            const th = document.createElement('th');
            th.className = i === 0 ? 'bf-name' : 'bf-task';
            if (i === 0 && leading.length === 1) th.classList.add('bf-last-sticky');
            th.textContent = label;
            tr.appendChild(th);
        });
        state.months.forEach((m, i) => {
            const th = document.createElement('th');
            th.className = 'text-end bf-month';
            if (state.withActuals && state.pastMonths.has(m)) th.classList.add('bf-actual-col');
            th.textContent = state.monthLabels[i] || m;
            tr.appendChild(th);
        });
        totals.forEach((label) => {
            const totTh = document.createElement('th');
            totTh.className = 'bf-total';
            totTh.textContent = label;
            tr.appendChild(totTh);
        });
        if (hasActions) {
            const actTh = document.createElement('th');
            actTh.className = 'bf-rowactions';
            tr.appendChild(actTh);
        }
        thead.appendChild(tr);
    }

    /** Blank sticky cells so a footer row lines up with the grid above it. */
    /**
     * How many leading (name / task) columns the staff grid currently has.
     * The footer is rebuilt on every cell save, long after `renderStaff` has
     * returned, so the count is kept here rather than threaded through.
     */
    let staffLeading = 1;

    /**
     * The ratio that turns staff cost into revenue.
     *
     * The non-staff lines are pass-through spend, so what is left of the budget
     * once they are taken out is what the staff effort is actually sold for.
     * Scaling a month's cost by `budget / (budget - other costs)` prices that
     * month's effort at its share of the contract.
     *
     * `null` when the project has no budget in EUR, or the other costs eat all
     * of it — the row then says so rather than inventing a figure.
     */
    function revenueFactor() {
        const indicators = (state.cashflow && state.cashflow.indicators) || {};
        const budget = parseFloat(indicators.budget);
        const other = parseFloat(indicators.other_cost) || 0;
        if (!Number.isFinite(budget) || budget <= 0) return null;
        const net = budget - other;
        if (net <= 0) return null;
        return budget / net;
    }

    function appendLeading(tr, leadingCount, label) {
        for (let i = 0; i < leadingCount; i += 1) {
            const td = document.createElement('td');
            td.className = i === 0 ? 'bf-name' : 'bf-task';
            if (i === 0 && leadingCount === 1) td.classList.add('bf-last-sticky');
            if (i === 0) td.textContent = label;
            tr.appendChild(td);
        }
    }

    /** A row's effective value for a month: the actual when one replaces it. */
    function cellValue(row, month) {
        if (state.withActuals && state.pastMonths.has(month)) {
            return parseFloat(row.actual_by_month[month]) || 0;
        }
        return parseFloat(row.by_month[month]) || 0;
    }

    function rowTotal(row) {
        return state.months.reduce((acc, m) => acc + cellValue(row, m), 0);
    }

    /** The EUR day rate in force for a row's person that month, or 0. */
    function dayRate(row, month) {
        const rates = state.staffRates[row.employee_id];
        return rates ? parseFloat(rates[month]) || 0 : 0;
    }

    /**
     * A row's days valued in EUR, month by month at that month's rate.
     *
     * `complete` is false as soon as a month carrying days has no rate — the
     * figure is then an understatement, and says so rather than passing for a
     * full total. Same rule as the cost grid's converted line.
     */
    function rowCost(row) {
        let total = 0;
        let complete = true;
        for (const m of state.months) {
            const days = cellValue(row, m);
            if (!days) continue;
            const rate = dayRate(row, m);
            if (!rate) { complete = false; continue; }
            total += days * rate;
        }
        return { total, complete };
    }

    function costCellText(cost) {
        if (!cost.total) return cost.complete ? '' : '—';
        return cost.complete ? fmtEur(cost.total) : `${fmtEur(cost.total)}*`;
    }

    /**
     * Four footer rows: the days, how they are spread over the months, what
     * they cost, and the revenue that cost stands for.
     *
     * The cost line is the same arithmetic as the cash-flow curve's staff
     * series — days x the person's internal day rate for that month — so the
     * grid can be added up against the chart below it without a mental FX step.
     */
    function buildStaffFoot() {
        const tfoot = staffTable.querySelector('tfoot');
        tfoot.innerHTML = '';
        if (!state.staffRows.length) return;

        // --- days -----------------------------------------------------------
        const dayRow = document.createElement('tr');
        appendLeading(dayRow, staffLeading, 'Total days');
        const daysByMonth = {};
        let grandDays = 0;
        for (const m of state.months) {
            const colTotal = state.staffRows.reduce((acc, r) => acc + cellValue(r, m), 0);
            daysByMonth[m] = colTotal;
            grandDays += colTotal;
            const td = document.createElement('td');
            td.className = 'text-end bf-monthtotal';
            if (state.withActuals && state.pastMonths.has(m)) td.classList.add('bf-actual-col');
            td.dataset.colTotal = m;
            td.textContent = fmtDays(colTotal);
            dayRow.appendChild(td);
        }
        const gDays = document.createElement('td');
        gDays.className = 'bf-total';
        gDays.dataset.grandTotal = 'days';
        gDays.textContent = fmtDays(grandDays);
        dayRow.appendChild(gDays);
        // The money column belongs to the cost row; leave its cell empty here.
        dayRow.appendChild(Object.assign(document.createElement('td'), { className: 'bf-total' }));
        tfoot.appendChild(dayRow);

        // --- how the effort is spread ---------------------------------------
        // Each month's share of the whole, and the share delivered by the end of
        // it — the shape of the workload, and how far along a given month is.
        const pctRow = document.createElement('tr');
        appendLeading(pctRow, staffLeading, '% (cum %)');
        pctRow.firstChild.title = 'Each month’s share of the project’s total days, '
            + 'and the cumulative share delivered up to and including that month.';
        let runningDays = 0;
        for (const m of state.months) {
            runningDays += daysByMonth[m];
            const td = document.createElement('td');
            td.className = 'text-end bf-monthtotal bf-pct';
            if (state.withActuals && state.pastMonths.has(m)) td.classList.add('bf-actual-col');
            td.textContent = grandDays && daysByMonth[m]
                ? `${fmtPct(daysByMonth[m] / grandDays)} (${fmtPct(runningDays / grandDays)})`
                : '';
            pctRow.appendChild(td);
        }
        const gPct = document.createElement('td');
        gPct.className = 'bf-total bf-pct';
        gPct.textContent = grandDays ? '100%' : '';
        pctRow.appendChild(gPct);
        pctRow.appendChild(Object.assign(document.createElement('td'), { className: 'bf-total' }));
        tfoot.appendChild(pctRow);

        // --- cost -----------------------------------------------------------
        let grandCost = 0;
        let complete = true;
        const byMonth = {};
        for (const m of state.months) {
            let monthCost = 0;
            for (const row of state.staffRows) {
                const days = cellValue(row, m);
                if (!days) continue;
                const rate = dayRate(row, m);
                if (!rate) { complete = false; continue; }
                monthCost += days * rate;
            }
            byMonth[m] = monthCost;
            grandCost += monthCost;
        }

        const costRow = document.createElement('tr');
        costRow.className = 'bf-eur-row';
        appendLeading(costRow, staffLeading, complete ? 'Cost' : 'Cost (partial)');
        costRow.firstChild.title = complete
            ? 'Days valued at each person’s internal day rate for that month.'
            : 'Some days belong to people with no internal day rate that month, and are left out.';
        for (const m of state.months) {
            const td = document.createElement('td');
            td.className = 'text-end bf-monthtotal';
            if (state.withActuals && state.pastMonths.has(m)) td.classList.add('bf-actual-col');
            td.textContent = byMonth[m] ? fmtEur(byMonth[m]) : '';
            costRow.appendChild(td);
        }
        // The days column belongs to the row above; leave its cell empty here.
        costRow.appendChild(Object.assign(document.createElement('td'), { className: 'bf-total' }));
        const gCost = document.createElement('td');
        gCost.className = 'bf-total';
        gCost.dataset.grandTotal = 'EUR';
        gCost.textContent = fmtEur(grandCost);
        costRow.appendChild(gCost);
        tfoot.appendChild(costRow);

        // --- revenue --------------------------------------------------------
        const factor = revenueFactor();
        const revRow = document.createElement('tr');
        appendLeading(revRow, staffLeading, 'Revenue');
        revRow.firstChild.title = factor
            ? 'Each month’s cost grossed up to revenue: cost × budget ÷ '
              + '(budget − other costs).'
            : 'Needs a project budget in EUR larger than the other costs; without one the '
              + 'cost cannot be grossed up to revenue.';
        for (const m of state.months) {
            const td = document.createElement('td');
            td.className = 'text-end bf-monthtotal';
            if (state.withActuals && state.pastMonths.has(m)) td.classList.add('bf-actual-col');
            td.textContent = factor && byMonth[m] ? fmtEur(byMonth[m] * factor) : '';
            revRow.appendChild(td);
        }
        revRow.appendChild(Object.assign(document.createElement('td'), { className: 'bf-total' }));
        const gRev = document.createElement('td');
        gRev.className = 'bf-total';
        gRev.dataset.grandTotal = 'revenue';
        gRev.textContent = factor ? fmtEur(grandCost * factor) : '—';
        revRow.appendChild(gRev);
        tfoot.appendChild(revRow);
    }

    function refreshStaffTotals() {
        for (const row of state.staffRows) {
            const key = `${row.employee_id}-${row.task_id === null ? 'none' : row.task_id}`;
            const daysCell = staffTable.querySelector(`[data-row-total="${key}"]`);
            if (daysCell) daysCell.textContent = fmtDays(rowTotal(row));
            const costCell = staffTable.querySelector(`[data-row-cost="${key}"]`);
            if (costCell) costCell.textContent = costCellText(rowCost(row));
        }
        buildStaffFoot();
    }

    function makeCell(row, month, value, onSave, afterSave, format) {
        const td = document.createElement('td');
        td.className = 'bf-cellwrap text-end';
        const input = document.createElement('input');
        input.type = 'number';
        input.step = 'any';
        input.min = '0';
        input.className = 'bf-cell';
        input.value = value ? format(value) : '';
        let previous = input.value;
        input.addEventListener('focus', () => { previous = input.value; });
        input.addEventListener('change', async () => {
            if (input.value === previous) return;
            input.classList.remove('is-error');
            input.classList.add('is-saving');
            try {
                const stored = await onSave(input.value);
                input.value = stored ? format(stored) : '';
                previous = input.value;
                if (stored) {
                    row.by_month[month] = stored;
                } else {
                    delete row.by_month[month];
                }
                // Totals only make sense once the server has confirmed the cell,
                // so recompute here rather than on the change event itself.
                if (afterSave) afterSave();
            } catch (err) {
                input.value = previous;
                input.classList.add('is-error');
                alert(`Could not save: ${err.message}`);
            } finally {
                input.classList.remove('is-saving');
            }
        });
        td.appendChild(input);
        return { td, input };
    }

    /** A past month in "actual + forecast" mode: what was logged, not editable. */
    function makeActualCell(row, month) {
        const td = document.createElement('td');
        td.className = 'bf-cellwrap text-end bf-actual-col';
        const span = document.createElement('span');
        const logged = row.actual_by_month[month];
        span.className = logged ? 'bf-actual' : 'bf-actual is-empty';
        span.textContent = logged ? fmtDays(logged) : '—';
        const budgeted = row.by_month[month];
        span.title = budgeted
            ? `Logged ${fmtDays(logged || 0)} d · budgeted ${fmtDays(budgeted)} d`
            : 'Days logged on this month (no forecast was entered)';
        td.appendChild(span);
        return td;
    }

    // --- staff grid ---------------------------------------------------------

    function renderStaff() {
        const tbody = staffTable.querySelector('tbody');
        tbody.innerHTML = '';
        const rows = state.staffRows;
        staffTable.style.display = rows.length ? '' : 'none';
        staffEmpty.style.display = rows.length ? 'none' : '';
        actualsLegend.style.display = state.withActuals ? '' : 'none';
        if (!rows.length) {
            staffTable.querySelector('thead').innerHTML = '';
            staffTable.querySelector('tfoot').innerHTML = '';
            return;
        }
        const leading = state.hasTasks ? ['Staff member', 'Task'] : ['Staff member'];
        staffLeading = leading.length;
        buildHead(staffTable, leading, false, ['Total days', 'Cost']);

        let previousEmployee = null;
        for (const row of rows) {
            const tr = document.createElement('tr');
            const isNewPerson = row.employee_id !== previousEmployee;
            if (isNewPerson && previousEmployee !== null) tr.classList.add('bf-group-start');

            const nameTd = document.createElement('td');
            nameTd.className = state.hasTasks ? 'bf-name' : 'bf-name bf-last-sticky';
            // With tasks, a person spans several rows; name them once per block.
            if (isNewPerson) {
                nameTd.textContent = row.employee;
                if (row.role) {
                    const role = document.createElement('span');
                    role.className = 'bf-role';
                    role.textContent = row.role;
                    nameTd.appendChild(role);
                }
            }
            tr.appendChild(nameTd);

            if (state.hasTasks) {
                const taskTd = document.createElement('td');
                taskTd.className = 'bf-task';
                taskTd.textContent = row.task || 'Project level';
                if (!row.task) taskTd.classList.add('text-muted');
                tr.appendChild(taskTd);
            }

            for (const month of state.months) {
                if (state.withActuals && state.pastMonths.has(month)) {
                    tr.appendChild(makeActualCell(row, month));
                    continue;
                }
                const { td } = makeCell(
                    row, month, row.by_month[month],
                    async (value) => {
                        const res = await postJson(urls.staffSave, {
                            employee_id: row.employee_id,
                            task_id: row.task_id,
                            month,
                            days: value,
                        });
                        return res.days;
                    },
                    () => { refreshStaffTotals(); scheduleCashflowRefresh(); },
                    fmtDays,
                );
                tr.appendChild(td);
            }

            const rowKey = `${row.employee_id}-${row.task_id === null ? 'none' : row.task_id}`;
            const totTd = document.createElement('td');
            totTd.className = 'bf-total';
            totTd.dataset.rowTotal = rowKey;
            totTd.textContent = fmtDays(rowTotal(row));
            tr.appendChild(totTd);

            const costTd = document.createElement('td');
            costTd.className = 'bf-total';
            costTd.dataset.rowCost = rowKey;
            const cost = rowCost(row);
            costTd.textContent = costCellText(cost);
            if (!cost.complete) {
                costTd.title = 'Some of these days have no internal day rate for '
                    + 'their month, and are not valued here.';
            }
            tr.appendChild(costTd);
            tbody.appendChild(tr);
            previousEmployee = row.employee_id;
        }
        buildStaffFoot();
    }

    // --- cost grid ----------------------------------------------------------

    /** Recompute the per-currency footer from the rows currently in state. */
    function rebuildCostTotals() {
        const byCurrency = {};
        // The converted total is built alongside, month by month, each at that
        // month's own rate — the rates come with the payload precisely so an
        // edit does not need a round trip to keep this row honest.
        const eur = { by_month: {}, total: 0, complete: true };
        for (const row of state.costRows) {
            const bucket = byCurrency[row.currency] || (byCurrency[row.currency] = { by_month: {}, total: 0 });
            const rates = state.costRates[row.currency] || {};
            for (const m of state.months) {
                const v = parseFloat(row.by_month[m]) || 0;
                if (!v) continue;
                bucket.by_month[m] = (bucket.by_month[m] || 0) + v;
                bucket.total += v;
                const rate = parseFloat(rates[m]);
                if (rate) {
                    eur.by_month[m] = (eur.by_month[m] || 0) + v * rate;
                    eur.total += v * rate;
                } else {
                    // A currency with no rate must not be quietly dropped from
                    // a total that claims to cover everything.
                    eur.complete = false;
                }
            }
        }
        state.costTotals = Object.keys(byCurrency).sort().map((code) => ({
            currency: code,
            by_month: byCurrency[code].by_month,
            total: byCurrency[code].total,
        }));
        state.costEur = eur;
        buildCostFoot();
    }

    function buildCostFoot() {
        const tfoot = costTable.querySelector('tfoot');
        tfoot.innerHTML = '';
        // One footer row per currency: without dated FX rates a single summed
        // figure across currencies would be a fiction.
        for (const bucket of state.costTotals) {
            const tr = document.createElement('tr');
            const td0 = document.createElement('td');
            td0.className = 'bf-name bf-last-sticky';
            td0.textContent = `Total ${bucket.currency}`;
            tr.appendChild(td0);
            for (const m of state.months) {
                const td = document.createElement('td');
                td.className = 'text-end bf-monthtotal';
                td.textContent = fmtMoney(bucket.by_month[m]);
                tr.appendChild(td);
            }
            const gTd = document.createElement('td');
            gTd.className = 'bf-total';
            gTd.dataset.grandTotal = bucket.currency;
            gTd.textContent = fmtMoney(bucket.total);
            tr.appendChild(gTd);
            tr.appendChild(document.createElement('td'));
            tfoot.appendChild(tr);
        }

        // The converted line is only worth showing once more than one currency
        // is in play — otherwise it just restates the row above it.
        const eur = state.costEur;
        const needsConversion = state.costTotals.some((t) => t.currency !== 'EUR');
        if (!eur || !state.costTotals.length || !needsConversion) return;
        const tr = document.createElement('tr');
        tr.className = 'bf-eur-row';
        const td0 = document.createElement('td');
        td0.className = 'bf-name bf-last-sticky';
        td0.textContent = eur.complete ? 'Total in EUR' : 'Total in EUR (partial)';
        td0.title = eur.complete
            ? 'Each month converted at that month\u2019s exchange rate.'
            : 'Some amounts are in a currency with no exchange rate, and are left out.';
        tr.appendChild(td0);
        for (const m of state.months) {
            const td = document.createElement('td');
            td.className = 'text-end bf-monthtotal';
            td.textContent = fmtMoney(eur.by_month[m]);
            tr.appendChild(td);
        }
        const gTd = document.createElement('td');
        gTd.className = 'bf-total';
        gTd.dataset.grandTotal = 'EUR-converted';
        gTd.textContent = fmtMoney(eur.total);
        tr.appendChild(gTd);
        tr.appendChild(document.createElement('td'));
        tfoot.appendChild(tr);
    }

    function refreshCostRow(row) {
        const cell = costTable.querySelector(`[data-row-total="${row.id}"]`);
        if (cell) cell.textContent = fmtMoney(sumValues(row.by_month));
        rebuildCostTotals();
    }

    function renderCosts() {
        const tbody = costTable.querySelector('tbody');
        tbody.innerHTML = '';
        const rows = state.costRows;
        costTable.style.display = rows.length ? '' : 'none';
        costEmpty.style.display = rows.length ? 'none' : '';
        if (!rows.length) {
            costTable.querySelector('thead').innerHTML = '';
            costTable.querySelector('tfoot').innerHTML = '';
            return;
        }
        buildHead(costTable, ['Cost line'], true);
        for (const row of rows) {
            const tr = document.createElement('tr');
            const nameTd = document.createElement('td');
            nameTd.className = 'bf-name bf-last-sticky';

            const labelWrap = document.createElement('div');
            labelWrap.className = 'd-flex gap-1 align-items-center';
            const label = document.createElement('input');
            label.type = 'text';
            label.className = 'bf-label';
            label.value = row.label;
            label.maxLength = 200;
            const currency = currencySelect(row.currency);
            labelWrap.appendChild(label);
            labelWrap.appendChild(currency);
            nameTd.appendChild(labelWrap);

            let prevLabel = row.label;
            let prevCurrency = row.currency;
            const saveLine = async (nextLabel, nextCurrency) => {
                const fd = new FormData();
                fd.append('line_id', row.id);
                fd.append('label', nextLabel);
                fd.append('currency', nextCurrency);
                const res = await postForm(urls.lineUpdate, fd);
                row.label = res.label;
                row.currency = res.currency;
                prevLabel = res.label;
                prevCurrency = res.currency;
            };

            label.addEventListener('change', async () => {
                const value = label.value.trim();
                if (!value) { label.value = prevLabel; return; }
                if (value === prevLabel) return;
                try {
                    await saveLine(value, prevCurrency);
                } catch (err) {
                    label.value = prevLabel;
                    alert(`Could not rename the line: ${err.message}`);
                }
            });

            currency.addEventListener('change', async () => {
                const next = currency.value;
                if (next === prevCurrency) return;
                // Amounts are relabelled, never converted — the app holds no
                // dated FX rates, so silently rescaling figures would be a lie.
                const hasAmounts = sumValues(row.by_month) > 0;
                if (hasAmounts && !confirm(
                    `Change "${row.label}" from ${prevCurrency} to ${next}?\n\n`
                    + 'The amounts already entered keep their figures and are simply '
                    + `read as ${next} — they are not converted.`,
                )) {
                    currency.value = prevCurrency;
                    return;
                }
                try {
                    await saveLine(label.value.trim() || prevLabel, next);
                    rebuildCostTotals();
                } catch (err) {
                    currency.value = prevCurrency;
                    alert(`Could not change the currency: ${err.message}`);
                }
            });

            tr.appendChild(nameTd);
            for (const month of state.months) {
                const { td } = makeCell(
                    row, month, row.by_month[month],
                    async (value) => {
                        const res = await postJson(urls.costSave, {
                            line_id: row.id, month, amount: value,
                        });
                        return res.amount;
                    },
                    () => { refreshCostRow(row); scheduleCashflowRefresh(); },
                    fmtMoney,
                );
                tr.appendChild(td);
            }
            const totTd = document.createElement('td');
            totTd.className = 'bf-total';
            totTd.dataset.rowTotal = row.id;
            totTd.textContent = fmtMoney(sumValues(row.by_month));
            tr.appendChild(totTd);

            const actTd = document.createElement('td');
            actTd.className = 'bf-rowactions';
            const del = document.createElement('button');
            del.type = 'button';
            del.className = 'btn btn-sm btn-link text-danger p-0';
            del.title = 'Delete this cost line';
            del.innerHTML = '<i class="fas fa-trash"></i>';
            del.addEventListener('click', async () => {
                const warn = sumValues(row.by_month)
                    ? `Delete "${row.label}" and its forecast amounts?`
                    : `Delete "${row.label}"?`;
                if (!confirm(warn)) return;
                const fd = new FormData();
                fd.append('line_id', row.id);
                try {
                    await postForm(urls.lineDelete, fd);
                    state.costRows = state.costRows.filter((r) => r.id !== row.id);
                    renderCosts();
                } catch (err) {
                    alert(`Could not delete the line: ${err.message}`);
                }
            });
            actTd.appendChild(del);
            tr.appendChild(actTd);
            tbody.appendChild(tr);
        }
        rebuildCostTotals();
    }

    // --- cash-flow forecast -------------------------------------------------

    function kpi(label, value, note, tone) {
        const box = document.createElement('div');
        box.className = `bf-kpi${tone ? ` is-${tone}` : ''}`;
        const l = document.createElement('span');
        l.className = 'bf-kpi-label';
        l.textContent = label;
        const v = document.createElement('span');
        v.className = 'bf-kpi-value';
        v.textContent = value;
        box.append(l, v);
        if (note) {
            const n = document.createElement('span');
            n.className = 'bf-kpi-note';
            n.textContent = note;
            box.appendChild(n);
        }
        return box;
    }

    function renderIndicators(cf) {
        const ind = cf.indicators;
        kpiWrap.innerHTML = '';
        const margin = parseFloat(ind.margin);
        const unscheduled = parseFloat(ind.unscheduled_revenue);

        kpiWrap.appendChild(kpi(
            'Budget', fmtEur(ind.budget),
            ind.budget ? 'Contract value, converted' : 'No budget on this project',
        ));
        kpiWrap.appendChild(kpi(
            // In "actual + forecast" the past is real spend, not a forecast; say so.
            state.withActuals ? 'Cost to date + forecast' : 'Forecast cost',
            fmtEur(ind.total_cost),
            `${fmtEur(ind.staff_cost)} staff \u00b7 ${fmtEur(ind.other_cost)} other`,
        ));
        // What a staffed day actually costs on this project, averaged over the
        // days that could be priced — the mix of who is on it, in one figure.
        kpiWrap.appendChild(kpi(
            'Avg internal day rate',
            ind.average_day_rate !== null ? fmtEur(ind.average_day_rate) : '\u2014',
            ind.average_day_rate !== null
                ? `Over ${fmtDays(ind.staff_days)} priced day(s)`
                : 'No days with a day rate yet',
        ));
        if (ind.margin !== null) {
            kpiWrap.appendChild(kpi(
                state.withActuals ? 'Margin at completion' : 'Gross margin',
                fmtEur(ind.margin),
                ind.margin_pct !== null ? `${ind.margin_pct}% of budget` : null,
                margin < 0 ? 'bad' : 'good',
            ));
        }
        // A gap between milestones and the contract means revenue is not fully
        // scheduled yet — worth seeing beside the figure, not buried.
        const gap = Number.isFinite(unscheduled) && Math.abs(unscheduled) >= 1;
        kpiWrap.appendChild(kpi(
            'Scheduled revenue', fmtEur(ind.scheduled_revenue),
            gap
                ? `${fmtEur(Math.abs(unscheduled))} ${unscheduled > 0 ? 'not yet scheduled' : 'over budget'}`
                : (ind.budget ? 'Matches the budget' : null),
            gap ? 'bad' : null,
        ));
        // CAPUR: revenue less the pass-through spend, i.e. what is left to cover
        // staff and carry the margin.
        kpiWrap.appendChild(kpi(
            'CAPUR', fmtEur(ind.capur),
            `Scheduled revenue less ${fmtEur(ind.other_cost)} of other costs`,
        ));
        // The other side of the same day: what the client pays for it. Over
        // *every* man-day, priced or not — CAPUR does not depend on day rates.
        kpiWrap.appendChild(kpi(
            'Ext avg day rate',
            ind.external_day_rate !== null ? fmtEur(ind.external_day_rate) : '\u2014',
            ind.external_day_rate !== null
                ? `CAPUR over ${fmtDays(ind.man_days)} man-day(s)`
                : 'No days forecast yet',
        ));
        kpiWrap.appendChild(kpi(
            'Peak funding need', fmtEur(ind.peak_funding_need),
            ind.peak_funding_month
                ? `Deepest at ${monthLabel(ind.peak_funding_month)}`
                : 'Never cash-negative',
            parseFloat(ind.peak_funding_need) > 0 ? 'bad' : 'good',
        ));
        kpiWrap.appendChild(kpi(
            'Break-even', ind.breakeven_month ? monthLabel(ind.breakeven_month) : '\u2014',
            ind.breakeven_month ? 'Cash turns positive for good' : 'Never, within these months',
            ind.breakeven_month ? null : 'bad',
        ));
    }

    /** Say plainly what the curve could not account for, rather than flatter it. */
    function renderCaveats(cf) {
        const c = cf.caveats;
        const notes = [];
        if (c.unpriced_days) {
            notes.push(`${fmtDays(c.unpriced_days)} forecast day(s) belong to people with no internal `
                + 'day rate for that month, so their cost is missing from the curve.');
        }
        if (c.undated_milestones && c.undated_milestones.length) {
            notes.push(`${c.undated_milestones.length} payment milestone(s) have no date and are `
                + `not plotted: ${c.undated_milestones.join(', ')}.`);
        }
        if (c.revenue_outside_window) {
            notes.push(`${fmtEur(c.revenue_outside_window)} of scheduled revenue is expected more `
                + 'than two years beyond the project, too far out to plot. It counts in the '
                + 'totals but has no bar — check the milestone dates.');
        }
        if (c.unconverted_cost_lines && c.unconverted_cost_lines.length) {
            notes.push(`No exchange rate for cost line(s): ${c.unconverted_cost_lines.join(', ')}.`);
        }
        if (c.unconverted_milestones && c.unconverted_milestones.length) {
            notes.push(`No exchange rate for milestone(s): ${c.unconverted_milestones.join(', ')}.`);
        }
        caveatBox.style.display = notes.length ? '' : 'none';
        caveatText.textContent = notes.join(' ');
    }

    function renderCashflow() {
        const cf = state.cashflow;
        if (!cf) return;
        renderIndicators(cf);
        renderCaveats(cf);

        const labels = cf.months.map(monthLabel);
        const staff = cf.months.map((m) => -Math.abs(parseFloat(cf.staff_cost[m]) || 0));
        const other = cf.months.map((m) => -Math.abs(parseFloat(cf.other_cost[m]) || 0));
        const revenue = cf.months.map((m) => parseFloat(cf.revenue[m]) || 0);
        const cumulative = cf.months.map((m) => parseFloat(cf.cumulative[m]) || 0);

        const anything = [...staff, ...other, ...revenue].some((v) => v !== 0);
        chartEmpty.style.display = anything ? 'none' : '';
        if (cashflowChart) { cashflowChart.destroy(); cashflowChart = null; }
        if (!anything) return;

        cashflowChart = new Chart(chartCanvas.getContext('2d'), {
            data: {
                labels,
                datasets: [
                    {
                        type: 'bar',
                        label: 'Revenue (milestones)',
                        data: revenue,
                        backgroundColor: 'rgba(2, 152, 143, 0.75)',
                        stack: 'flow',
                        order: 3,
                    },
                    {
                        type: 'bar',
                        label: 'Staff cost',
                        data: staff,
                        backgroundColor: 'rgba(176, 42, 55, 0.75)',
                        stack: 'flow',
                        order: 3,
                    },
                    {
                        type: 'bar',
                        label: 'Other costs',
                        data: other,
                        backgroundColor: 'rgba(255, 128, 0, 0.7)',
                        stack: 'flow',
                        order: 3,
                    },
                    {
                        type: 'line',
                        label: 'Cumulative cash position',
                        data: cumulative,
                        borderColor: '#2b3a55',
                        backgroundColor: '#2b3a55',
                        borderWidth: 2,
                        pointRadius: 2,
                        tension: 0.2,
                        order: 1,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                scales: {
                    // Costs are drawn below the line and revenue above, so the
                    // shape of a month reads at a glance; one axis keeps the
                    // cumulative line comparable with the bars.
                    x: { stacked: true, grid: { display: false } },
                    y: {
                        stacked: true,
                        ticks: { callback: (v) => EUR.format(v) },
                        grid: { color: 'rgba(0,0,0,0.06)' },
                    },
                },
                plugins: {
                    legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => `${ctx.dataset.label}: ${fmtEur(Math.abs(ctx.parsed.y))}`,
                        },
                    },
                },
            },
        });
    }

    // --- loading ------------------------------------------------------------

    async function load() {
        const withActuals = modeInputs.some((i) => i.checked && i.value === 'actuals');
        const url = withActuals ? `${urls.data}?actuals=1` : urls.data;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const data = await res.json();
        state = {
            months: data.months || [],
            monthLabels: data.month_labels || [],
            pastMonths: new Set(data.past_months || []),
            staffRows: data.staff_rows || [],
            staffRates: data.staff_rates || {},
            costRows: data.cost_rows || [],
            costTotals: (data.cost_totals || []).map((t) => ({
                currency: t.currency,
                by_month: Object.fromEntries(
                    Object.entries(t.by_month).map(([m, v]) => [m, parseFloat(v)]),
                ),
                total: parseFloat(t.total),
            })),
            costRates: data.cost_rates || {},
            costEur: null,
            cashflow: data.cashflow || null,
            hasTasks: !!data.has_tasks,
            withActuals: !!data.with_actuals,
            currencies: data.currencies || ['EUR'],
            currency: data.currency || 'EUR',
        };
        noDates.style.display = data.has_dates ? 'none' : '';
        content.style.display = data.has_dates ? '' : 'none';
        if (!data.has_dates) return;

        newLineCurrency.innerHTML = '';
        for (const code of state.currencies) {
            const opt = document.createElement('option');
            opt.value = code;
            opt.textContent = code;
            if (code === state.currency) opt.selected = true;
            newLineCurrency.appendChild(opt);
        }
        renderStaff();
        renderCosts();
        renderCashflow();
    }

    /** Editing a cell moves the curve; refresh it from the server after a save. */
    let cashflowTimer = null;
    function scheduleCashflowRefresh() {
        clearTimeout(cashflowTimer);
        cashflowTimer = setTimeout(async () => {
            const withActuals = modeInputs.some((i) => i.checked && i.value === 'actuals');
            const url = withActuals ? `${urls.data}?actuals=1` : urls.data;
            try {
                const res = await fetch(url, { headers: { Accept: 'application/json' } });
                if (!res.ok) return;
                const data = await res.json();
                state.cashflow = data.cashflow || null;
                renderCashflow();
                // The revenue footer row is grossed up from the cost with the
                // budget and other costs the payload just refreshed.
                buildStaffFoot();
            } catch {
                // A stale chart beats a broken page; the next edit retries.
            }
        }, 600);
    }

    modeInputs.forEach((input) => input.addEventListener('change', () => { load(); }));

    addLineBtn.addEventListener('click', async () => {
        const label = newLineInput.value.trim();
        if (!label) { newLineInput.focus(); return; }
        const fd = new FormData();
        fd.append('label', label);
        fd.append('currency', newLineCurrency.value);
        addLineBtn.disabled = true;
        try {
            const res = await postForm(urls.lineAdd, fd);
            state.costRows.push({
                id: res.id, label: res.label, currency: res.currency, by_month: {},
            });
            newLineInput.value = '';
            renderCosts();
        } catch (err) {
            alert(`Could not add the line: ${err.message}`);
        } finally {
            addLineBtn.disabled = false;
        }
    });
    newLineInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); addLineBtn.click(); }
    });

    // The tab is hidden on page load; fetch the first time it is opened.
    function loadOnce() {
        if (loaded) return;
        loaded = true;
        load();
    }
    document.querySelectorAll('.project-tab[data-tab="budget-forecast"]')
        .forEach((btn) => btn.addEventListener('click', loadOnce));
    if (window.location.hash === '#budget-forecast') loadOnce();
}
